//@ts-check
/**
 * Maps abstract LOD levels onto the source octree levels (and point ranges) that a
 * point-cloud fragment must load. All behaviour that differs between the two LOD
 * mapping modes lives here, behind the {@link LodMapping} interface:
 *
 * - 'direct'    — each LOD level maps onto fixed source octree level(s); the range
 *                 returned by {@link LodMapping.getSourceRange} are source LEVEL indices.
 * - 'threshold' — LOD levels are defined by cumulative point-count thresholds; the
 *                 range is cumulative POINT indices.
 *
 * In 'direct' mode the number of coarsest source levels combined into LOD 0 is chosen
 * at load time via {@link computeDirectModeLevelsInLod0} and passed as `levelsInLod0`
 * to the exported helpers.
 *
 * The two implementations are fully self-contained. To remove a mode once the
 * distinction is no longer needed, delete its strategy object and the corresponding
 * branch of the {@link lodMapping} selector at the bottom of this file — no other
 * file needs to change.
 */

import {
  LOD_MAPPING_MODE,
  MIN_LEVELS_IN_LOD0,
  RCS_LOD_THRESHOLDS,
  RCS_MAX_OCTREE_LEVELS,
  RCS_BYTES_PER_POINT,
  RCS_LOD0_TARGET_MAX_BYTES,
  RCS_HEADER_BYTES,
  UMBRELLA_LEAF_RECORD_BYTES,
  OCTREE_CHUNK_LEAF_COUNT_BYTES,
  RCS_BYTES_PER_SVO_POINT,
  OPFS_METADATA_BYTES_PER_ENTRY } from
'./RcsConstants.js';

/**
 * Source levels (and point totals) a fragment must load for a single LOD level.
 * @typedef {Object} LevelsToProcessResult
 * @property {number[]} levelsToProcess   - Source octree level indices to load.
 * @property {number}   pointsBeforeStart - Points contained in levels before the first loaded one.
 * @property {number}   totalPoints       - Total points across {@link levelsToProcess}.
 */

/**
 * Inclusive source range a single LOD level loads. The unit is mode-specific:
 * source LEVEL indices in 'direct' mode, cumulative POINT indices in 'threshold' mode.
 * @typedef {Object} LodSourceRange
 * @property {number} startPointOrLevel - Start of the range (source level index in 'direct'
 *   mode, cumulative point index in 'threshold' mode).
 * @property {number} endPointOrLevel - End of the range (source level index in 'direct'
 *   mode, cumulative point index in 'threshold' mode).
 */

/**
 * Limits for "pulling up" a leaf's points into ancestor interior nodes (HLOD).
 * @typedef {Object} InnerNodePullUpLimit
 * @property {number} maxLevel  - Exclusive upper bound on the source levels to pull up.
 * @property {number} maxPoints - Stop pulling up once this many points have been assigned.
 */

/**
 * @typedef {Object} LodMapping
 * @property {(levelsInLod0: number, lodLevel: number) => LodSourceRange} getSourceRange
 *   The {@link LodSourceRange} a fragment loads for the given LOD level. `levelsInLod0`
 *   is ignored in 'threshold' mode.
 * @property {(levelsInLod0: number, numSvoLeafNodes: number[]) => number[]} getRequiredLodLevels
 *   The LOD level indices that should be created for a node with the given per-source-level
 *   SVO leaf-node counts. `levelsInLod0` is ignored in 'threshold' mode.
 * @property {(numSvoLeafNodes: number[], startPointOrLevel: number, endPointOrLevel: number) => LevelsToProcessResult} calculateLevelsToProcess
 *   Resolves a source range into the concrete list of source levels to process.
 * @property {(levelsInLod0: number, numSvoLeafNodes: number[], lodLevel: number) => number} getStartSourceLevel
 *   First source octree level loaded for the given LOD level (used for feature-size sizing).
 *   `levelsInLod0` is ignored in 'threshold' mode.
 * @property {(levelsInLod0: number, numSvoLeafNodes: number[]) => InnerNodePullUpLimit} getInnerNodePullUpLimit
 *   Bounds for the HLOD point pull-up into interior nodes. `levelsInLod0` is ignored in
 *   'threshold' mode.
 */

/**
 * Minimal shape consumed by {@link computeDirectModeLevelsInLod0}: any octree leaf that
 * exposes per-level SVO leaf point counts. Both `UmbrellaLeafNode` and `OctreeLeafNode`
 * satisfy this.
 * @typedef {{ numSvoLeafNodes: number[] }} LeafWithLevelCounts
 */

// ── 'direct' mode ────────────────────────────────────────────────────────────

/** @type {LodMapping} */
const directLodMapping = {
  getSourceRange(levelsInLod0, lodLevel) {
    if (levelsInLod0 < MIN_LEVELS_IN_LOD0) {
      return { startPointOrLevel: 0, endPointOrLevel: -1 };
    }
    const maxLodLevels = RCS_MAX_OCTREE_LEVELS - levelsInLod0 + 1;
    if (lodLevel < 0 || lodLevel >= maxLodLevels) {
      throw new Error(`Invalid LOD level: ${lodLevel}. Must be 0-${maxLodLevels - 1}`);
    }
    // LOD 0 combines the `levelsInLod0` coarsest source levels (they hold
    // few points each); LOD 1+ map one-to-one onto the remaining source levels.
    if (lodLevel === 0) {
      return { startPointOrLevel: 0, endPointOrLevel: levelsInLod0 - 1 };
    }
    const sourceLevel = levelsInLod0 + (lodLevel - 1);
    return { startPointOrLevel: sourceLevel, endPointOrLevel: sourceLevel };
  },

  getRequiredLodLevels(levelsInLod0, numSvoLeafNodes) {
    if (levelsInLod0 < MIN_LEVELS_IN_LOD0) {
      return [];
    }

    const lodLevels = [];

    // LOD 0 must exist for every node that has any points at all, otherwise we get
    // holes when a node keeps all its points in higher levels and LOD 0 is skipped.
    const hasAnyPoints = numSvoLeafNodes.some((count) => count > 0);
    if (hasAnyPoints) {
      lodLevels.push(0);
    }

    for (let level = levelsInLod0; level < numSvoLeafNodes.length; level++) {
      if (numSvoLeafNodes[level] > 0) {
        lodLevels.push(1 + (level - levelsInLod0));
      }
    }

    return lodLevels;
  },

  calculateLevelsToProcess(numSvoLeafNodes, startLevel, endLevel) {
    let pointsBeforeStart = 0;
    for (let level = 0; level < startLevel && level < numSvoLeafNodes.length; level++) {
      pointsBeforeStart += numSvoLeafNodes[level];
    }

    if (startLevel >= numSvoLeafNodes.length || endLevel < 0) {
      return { levelsToProcess: [], pointsBeforeStart, totalPoints: 0 };
    }

    const levelsToProcess = [];
    let totalPoints = 0;
    for (let level = startLevel; level <= endLevel && level < numSvoLeafNodes.length; level++) {
      levelsToProcess.push(level);
      totalPoints += numSvoLeafNodes[level];
    }

    return { levelsToProcess, pointsBeforeStart, totalPoints };
  },

  getStartSourceLevel(levelsInLod0, numSvoLeafNodes, lodLevel) {
    if (levelsInLod0 < MIN_LEVELS_IN_LOD0) {
      throw new Error(`getStartSourceLevel: no source level for LOD ${lodLevel} on a point-less node`);
    }
    // The range start is itself the first source level in direct mode.
    return this.getSourceRange(levelsInLod0, lodLevel).startPointOrLevel;
  },

  getInnerNodePullUpLimit(levelsInLod0, numSvoLeafNodes) {
    if (levelsInLod0 < MIN_LEVELS_IN_LOD0) {
      return { maxLevel: 0, maxPoints: 0 };
    }
    // Pull up exactly the LOD-0 source range; no point cap.
    return { maxLevel: levelsInLod0, maxPoints: Infinity };
  }
};

// ── 'threshold' mode ──────────────────────────────────────────────────────────

/**
 * Threshold mode: find the first source level to load for a cumulative point start.
 *
 * - startPoint === 0: include level 0 (initial load).
 * - startPoint > 0: skip the level that contains startPoint (it was loaded by the previous
 *   LOD) and start from the next one, so points are never loaded twice.
 *
 * @param {number[]} numSvoLeafNodes - Per-source-level SVO leaf-node counts.
 * @param {number} startPoint - Cumulative point index to start from.
 * @returns {{ startLevel: number, pointsBeforeStart: number }} `startLevel` is -1 if startPoint
 *   is beyond all points.
 */
function thresholdStartLevel(numSvoLeafNodes, startPoint) {
  let pointsBeforeStart = 0;
  let startLevel = -1;

  for (let level = 0; level < numSvoLeafNodes.length; level++) {
    const leafNodesInLevel = numSvoLeafNodes[level];
    if (pointsBeforeStart + leafNodesInLevel > startPoint) {
      if (startPoint === 0) {
        startLevel = level;
      } else {
        // Skip the (already loaded) level containing startPoint and start at the next,
        // accounting for its points so the caller's start offset stays correct.
        startLevel = level + 1;
        pointsBeforeStart += leafNodesInLevel;
      }
      break;
    }
    pointsBeforeStart += leafNodesInLevel;
  }

  return { startLevel, pointsBeforeStart };
}

/** @type {LodMapping} */
const thresholdLodMapping = {
  getSourceRange(_levelsInLod0, lodLevel) {
    if (lodLevel < 0 || lodLevel >= RCS_LOD_THRESHOLDS.length) {
      throw new Error(`Invalid LOD level: ${lodLevel}. Must be 0-${RCS_LOD_THRESHOLDS.length - 1}`);
    }
    // These ranges are DISPLAY thresholds (when to switch LODs), not literal load ranges:
    // the actual loaded range is clamped to whole levels by calculateLevelsToProcess.
    const startPoint = lodLevel === 0 ? 0 : RCS_LOD_THRESHOLDS[lodLevel - 1];
    const endPoint = RCS_LOD_THRESHOLDS[lodLevel];
    return { startPointOrLevel: startPoint, endPointOrLevel: endPoint };
  },

  getRequiredLodLevels(_levelsInLod0, numSvoLeafNodes) {
    const totalPoints = numSvoLeafNodes.reduce((sum, count) => sum + count, 0);
    const lodLevels = [];
    for (let lod = 0; lod < RCS_LOD_THRESHOLDS.length; lod++) {
      const startPoint = lod === 0 ? 0 : RCS_LOD_THRESHOLDS[lod - 1];
      if (totalPoints > startPoint) {
        lodLevels.push(lod);
      } else {
        break;
      }
    }
    return lodLevels;
  },

  calculateLevelsToProcess(numSvoLeafNodes, startPoint, endPoint) {
    const { startLevel, pointsBeforeStart } = thresholdStartLevel(numSvoLeafNodes, startPoint);

    if (startLevel === -1 || startLevel >= numSvoLeafNodes.length) {
      return { levelsToProcess: [], pointsBeforeStart, totalPoints: 0 };
    }

    // If the previous LOD already loaded past this LOD's endPoint, load nothing.
    // (e.g. LOD 0 loaded a whole 21 777-point level while LOD 1's range is 1 000-10 000.)
    if (pointsBeforeStart >= endPoint) {
      return { levelsToProcess: [], pointsBeforeStart, totalPoints: 0 };
    }

    const levelsToProcess = [];
    let totalPoints = 0;
    for (let level = startLevel; level < numSvoLeafNodes.length; level++) {
      levelsToProcess.push(level);
      totalPoints += numSvoLeafNodes[level];
      // Continue until the cumulative point count from the file start reaches endPoint.
      if (pointsBeforeStart + totalPoints > endPoint) {
        break;
      }
    }

    return { levelsToProcess, pointsBeforeStart, totalPoints };
  },

  getStartSourceLevel(_levelsInLod0, numSvoLeafNodes, lodLevel) {
    const { startPointOrLevel } = this.getSourceRange(_levelsInLod0, lodLevel);
    const { startLevel } = thresholdStartLevel(numSvoLeafNodes, startPointOrLevel);
    if (startLevel === -1) {
      throw new Error(`getStartSourceLevel: no source level found for LOD ${lodLevel}`);
    }
    return startLevel;
  },

  getInnerNodePullUpLimit(_levelsInLod0, numSvoLeafNodes) {
    // All levels are eligible, but stop once ~1 000 points have been pulled up.
    return { maxLevel: numSvoLeafNodes.length, maxPoints: 1_000 };
  }
};

// ── Active strategy ───────────────────────────────────────────────────────────

/**
 * The active LOD mapping, selected once from {@link LOD_MAPPING_MODE}. This is the only
 * place that branches on the mode.
 * @type {LodMapping}
 */
const lodMapping = LOD_MAPPING_MODE === 'direct' ? directLodMapping : thresholdLodMapping;

/**
 * @param {number[]} numSvoLeafNodes - Per-source-level SVO leaf-node counts.
 * @param {number} lodLevel - Target LOD level index.
 * @param {number} [levelsInLod0] - LOD-0 source level count ('direct' mode only).
 * @returns {LevelsToProcessResult}
 */
export function calculateLevelsToProcessForLod(numSvoLeafNodes, lodLevel, levelsInLod0 = MIN_LEVELS_IN_LOD0) {
  const { startPointOrLevel, endPointOrLevel } = lodMapping.getSourceRange(levelsInLod0, lodLevel);
  return lodMapping.calculateLevelsToProcess(numSvoLeafNodes, startPointOrLevel, endPointOrLevel);
}

/**
 * @param {number[]} numSvoLeafNodes - Per-source-level SVO leaf-node counts.
 * @param {number} [levelsInLod0] - LOD-0 source level count ('direct' mode only).
 * @returns {number[]} LOD level indices that should be created for the node.
 */
export function getRequiredLodLevels(numSvoLeafNodes, levelsInLod0 = MIN_LEVELS_IN_LOD0) {
  return lodMapping.getRequiredLodLevels(levelsInLod0, numSvoLeafNodes);
}

/**
 * @param {number[]} numSvoLeafNodes - Per-source-level SVO leaf-node counts.
 * @param {number} lodLevel
 * @param {number} [levelsInLod0] - LOD-0 source level count ('direct' mode only).
 * @returns {number} First source octree level loaded for the LOD level.
 */
export function getStartSourceLevel(numSvoLeafNodes, lodLevel, levelsInLod0 = MIN_LEVELS_IN_LOD0) {
  return lodMapping.getStartSourceLevel(levelsInLod0, numSvoLeafNodes, lodLevel);
}

/**
 * @param {number[]} numSvoLeafNodes - Per-source-level SVO leaf-node counts.
 * @param {number} [levelsInLod0] - LOD-0 source level count ('direct' mode only).
 * @returns {InnerNodePullUpLimit} Bounds for the HLOD point pull-up into interior nodes.
 */
export function getInnerNodePullUpLimit(numSvoLeafNodes, levelsInLod0 = MIN_LEVELS_IN_LOD0) {
  return lodMapping.getInnerNodePullUpLimit(levelsInLod0, numSvoLeafNodes);
}

/**
 * Build a global per-source-level point histogram across all umbrella leaves.
 *
 * @param {LeafWithLevelCounts[]} umbrellaLeafNodes
 * @returns {{ totalPointsPerLevel: number[], lastLevelWithPoints: number }}
 */
function buildGlobalPointsPerLevelHistogram(umbrellaLeafNodes) {
  const totalPointsPerLevel = new Array(RCS_MAX_OCTREE_LEVELS).fill(0);
  let lastLevelWithPoints = -1;
  for (const leaf of umbrellaLeafNodes) {
    const counts = leaf?.numSvoLeafNodes;
    if (!counts) continue;
    for (let level = 0; level < counts.length; level++) {
      const count = counts[level];
      if (count > 0) {
        totalPointsPerLevel[level] += count;
        if (level > lastLevelWithPoints) lastLevelWithPoints = level;
      }
    }
  }
  return { totalPointsPerLevel, lastLevelWithPoints };
}

/**
 * Count umbrella leaves that have at least one point at the given source level.
 *
 * @param {LeafWithLevelCounts[]} umbrellaLeafNodes
 * @param {number} sourceLevel
 * @returns {number}
 */
function countLeavesWithPointsAtLevel(umbrellaLeafNodes, sourceLevel) {
  let count = 0;
  for (const leaf of umbrellaLeafNodes) {
    if ((leaf?.numSvoLeafNodes?.[sourceLevel] ?? 0) > 0) {
      count++;
    }
  }
  return count;
}

/**
 * Decide the deepest LOD level whose raw SVO fragments may be written to the per-file OPFS
 * cache under a byte budget ('direct' mode only).
 *
 * Bootstrap blobs (header, octree, params) and LOD-0 decoded geometry (inner nodes and leaf
 * overflow) are always counted toward the budget but are never excluded — the return value is
 * floored at `0` (LOD index 0, which already spans source levels `[0, levelsInLod0)` and the
 * HLOD pull-up tier), meaning finer raw SVO tiers (LOD ≥ 1) may be excluded when the base
 * alone exceeds the cap.
 *
 * The transient LOD-0 bucket (raw undecoded SVO bytes, deleted once all inner nodes are
 * cached) is deliberately excluded: it is a temporary cold-load aid, not steady-state footprint.
 *
 * @param {LeafWithLevelCounts[]} umbrellaLeafNodes - Per-umbrella-leaf metadata
 * @param {number} levelsInLod0 - Coarsest source levels folded into LOD 0
 * @param {number} budgetBytes - Per-file cache cap in bytes; `0` or non-finite means unlimited
 * @returns {number} Highest cacheable LOD index, or `Infinity` when unlimited / not in 'direct' mode
 */
export function computeMaxCachedLodLevel(umbrellaLeafNodes, levelsInLod0, budgetBytes) {
  if (LOD_MAPPING_MODE !== 'direct' || !budgetBytes || !Number.isFinite(budgetBytes) || budgetBytes <= 0) {
    return Infinity;
  }
  if (!umbrellaLeafNodes || umbrellaLeafNodes.length === 0 || levelsInLod0 < MIN_LEVELS_IN_LOD0) {
    return 0;
  }

  const { totalPointsPerLevel, lastLevelWithPoints } = buildGlobalPointsPerLevelHistogram(umbrellaLeafNodes);
  if (lastLevelWithPoints < 0) {
    return 0;
  }

  const numLeafs = umbrellaLeafNodes.length;

  // Base footprint: bootstrap blobs + LOD-0 decoded geometry (inner nodes + leaf overflow).
  // The octree blob is chunk 2 verbatim: a leaf-count prefix plus one record per umbrella leaf.
  let cumulativeBytes = RCS_HEADER_BYTES +
  OCTREE_CHUNK_LEAF_COUNT_BYTES + numLeafs * UMBRELLA_LEAF_RECORD_BYTES;
  for (let level = 0; level < levelsInLod0; level++) {
    cumulativeBytes += totalPointsPerLevel[level] * RCS_BYTES_PER_POINT;
  }

  const maxLodLevel = lastLevelWithPoints - levelsInLod0 + 1;
  if (maxLodLevel <= 0) {
    return 0;
  }

  let maxCachedLodLevel = 0;
  for (let lodLevel = 1; lodLevel <= maxLodLevel; lodLevel++) {
    const sourceLevel = levelsInLod0 + lodLevel - 1;
    const pointsAtLevel = totalPointsPerLevel[sourceLevel];
    const leavesAtLevel = countLeavesWithPointsAtLevel(umbrellaLeafNodes, sourceLevel);
    const levelBytes = pointsAtLevel * RCS_BYTES_PER_SVO_POINT +
    leavesAtLevel * OPFS_METADATA_BYTES_PER_ENTRY;
    if (cumulativeBytes + levelBytes > budgetBytes) {
      break;
    }
    cumulativeBytes += levelBytes;
    maxCachedLodLevel = lodLevel;
  }

  // LOD 0 already covers source levels [0, levelsInLod0); maxCachedLodLevel only gates LOD ≥ 1.
  return Math.max(maxCachedLodLevel, 0);
}

/**
 * Auto-select the number of coarsest source octree levels to combine into LOD 0
 * ('direct' mode) based on the size of the model.
 *
 * LOD 0 is the coarsest level, loaded initially and pulled up into ancestor nodes.
 * Its in-memory footprint equals the total number of points across source levels
 * `0 .. levelsInLod0 - 1` (summed over all umbrella leaves) times {@link RCS_BYTES_PER_POINT}.
 *
 * Because that footprint grows monotonically with `levelsInLod0`, we pick the largest
 * value whose footprint stays within `maxBytes`, floored at {@link MIN_LEVELS_IN_LOD0}
 * (LOD 0 must always include at least source level 0, even if that alone exceeds the cap).
 * Maximizing under the cap pulls up as much detail as fits; there is no separately
 * enforced lower bound, so a small model may fold its whole cloud into LOD 0 and a model
 * whose next level would overshoot the cap simply stays below it.
 *
 * @param {LeafWithLevelCounts[]} umbrellaLeafNodes - Per-umbrella-leaf metadata (uses `numSvoLeafNodes`)
 * @param {Object} [opts]
 * @param {number} [opts.maxBytes=RCS_LOD0_TARGET_MAX_BYTES] - Upper bound on the LOD-0 in-memory footprint
 * @param {number} [opts.bytesPerPoint=RCS_BYTES_PER_POINT] - In-memory size of a single decoded point
 * @returns {number} Number of source levels to combine into LOD 0, or `0` when the mode
 *   is not 'direct' or the model contains no points (in which case the value is never consumed).
 */
export function computeDirectModeLevelsInLod0(umbrellaLeafNodes, opts = {}) {
  const maxBytes = opts.maxBytes ?? RCS_LOD0_TARGET_MAX_BYTES;
  const bytesPerPoint = opts.bytesPerPoint ?? RCS_BYTES_PER_POINT;

  if (LOD_MAPPING_MODE !== 'direct' || !umbrellaLeafNodes || umbrellaLeafNodes.length === 0) {
    return 0;
  }

  const { totalPointsPerLevel, lastLevelWithPoints } = buildGlobalPointsPerLevelHistogram(umbrellaLeafNodes);

  // Model has no points at all — value is irrelevant and never consumed.
  if (lastLevelWithPoints < 0) return 0;

  // Pick the number of levels in lod0 in [MIN_LEVELS_IN_LOD0, lastLevelWithPoints + 1]
  // whose cumulative footprint stays within maxBytes. N is floored at MIN_LEVELS_IN_LOD0
  // and capped at lastLevelWithPoints + 1 so LOD 0 never spans phantom empty trailing levels.
  const maxN = lastLevelWithPoints + 1;
  let cumulativePoints = 0;
  let levelsInLod0 = MIN_LEVELS_IN_LOD0;
  for (let n = MIN_LEVELS_IN_LOD0; n <= maxN; n++) {
    cumulativePoints += totalPointsPerLevel[n - 1];
    if (n > MIN_LEVELS_IN_LOD0 && cumulativePoints * bytesPerPoint > maxBytes) {
      break;
    }
    levelsInLod0 = n;
  }

  return levelsInLod0;
}